import { useEffect, useRef, useState } from "react";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Upload, Trash2, Image as ImageIcon, Film, Loader2, X, Check, Zap, Clock as ClockIcon, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const minutesToHHMM = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

// 12-hour helpers
const to24 = (h, p) => (p === "AM" ? h % 12 : (h % 12) + 12);
const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES_5 = Array.from({ length: 12 }, (_, i) => i * 5);

const fmtSize = (b) =>
  b >= 1024 * 1024 * 1024
    ? `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`
    : b >= 1024 * 1024
    ? `${(b / 1024 / 1024).toFixed(1)} MB`
    : `${(b / 1024).toFixed(0)} KB`;

export default function QuickContentDialog({ tv, businessId, onClose, onChanged }) {
  const open = !!tv;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quick-content-dialog">
        {open && (
          <QuickContentBody
            key={tv.id}
            tv={tv}
            businessId={businessId}
            onClose={onClose}
            onChanged={onChanged}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function QuickContentBody({ tv, businessId, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mode: 24h loop vs scheduled time window
  const [mode24h, setMode24h] = useState(false);
  const [alwaysOnMedia, setAlwaysOnMedia] = useState([]);
  const [alwaysOnPlaylistId, setAlwaysOnPlaylistId] = useState(null);

  // Upload state
  const [pending, setPending] = useState(null); // { file, mediaId, progress, error }
  const [pendingBatch, setPendingBatch] = useState([]); // [{file, mediaId, progress, error}]
  const fileInputRef = useRef(null);
  const fileInputRef24 = useRef(null);

  // Schedule state (defaults: Mon-Fri 09:00 AM – 09:00 PM)
  const [days, setDays] = useState([0, 1, 2, 3, 4]);
  const [startH, setStartH] = useState(9);
  const [startM, setStartM] = useState(0);
  const [startP, setStartP] = useState("AM");
  const [endH, setEndH] = useState(9);
  const [endM, setEndM] = useState(0);
  const [endP, setEndP] = useState("PM");
  const [itemDuration, setItemDuration] = useState(8);
  const [itemDuration24, setItemDuration24] = useState(8);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await apiAdmin.get(`/tvs/${tv.id}/quick-content`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const reload24 = async () => {
    try {
      const { data } = await apiAdmin.get(`/tvs/${tv.id}/quick-content/24h`);
      setAlwaysOnMedia(data?.media || []);
      setAlwaysOnPlaylistId(data?.playlist?.id || null);
      if (data?.playlist?.item_duration) setItemDuration24(data.playlist.item_duration);
    } catch (e) {
      // Silent; show empty
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    Promise.all([
      apiAdmin.get(`/tvs/${tv.id}/quick-content`, { signal: ac.signal })
        .then(({ data }) => setItems(data || [])),
      apiAdmin.get(`/tvs/${tv.id}/quick-content/24h`, { signal: ac.signal })
        .then(({ data }) => {
          setAlwaysOnMedia(data?.media || []);
          setAlwaysOnPlaylistId(data?.playlist?.id || null);
          if (data?.playlist?.item_duration) setItemDuration24(data.playlist.item_duration);
          // If TV already has 24h items, default to that mode
          if ((data?.media || []).length > 0) setMode24h(true);
        }),
    ])
      .catch((e) => { if (!ac.signal.aborted) toast.error(formatApiError(e)); })
      .finally(() => { if (!ac.signal.aborted) setLoading(false); });
    return () => ac.abort();
  }, [tv.id]);

  const toggleDay = (d) =>
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()));

  const onPickFile = () => fileInputRef.current?.click();

  const startMinutes = to24(startH, startP) * 60 + startM;
  const endMinutes = to24(endH, endP) * 60 + endM;
  const sameTime = startMinutes === endMinutes;

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = ""; // allow re-uploading the same file
    setPending({ file: f, progress: 0, mediaId: null, error: null });

    try {
      // 1) signed URL
      const { data: init } = await apiAdmin.post("/media/upload-url", {
        business_id: businessId,
        filename: f.name,
        content_type: f.type || "application/octet-stream",
      });
      // 2) PUT directly to Supabase with progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", init.signed_url, true);
        xhr.setRequestHeader("Content-Type", f.type || "application/octet-stream");
        xhr.setRequestHeader("x-upsert", "true");
        xhr.upload.onprogress = (ev) => {
          if (!ev.lengthComputable) return;
          setPending((p) => (p ? { ...p, progress: Math.round((ev.loaded / ev.total) * 100) } : p));
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve(null)
            : reject(new Error(`Supabase upload failed (${xhr.status})`));
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.send(f);
      });
      // 3) Finalize -> media row exists
      const { data: media } = await apiAdmin.post("/media/upload-finalize", {
        media_id: init.media_id,
        object_path: init.object_path,
        business_id: businessId,
        filename: f.name,
        content_type: f.type || "application/octet-stream",
        size: f.size,
      });
      setPending({ file: f, progress: 100, mediaId: media.id, error: null });
      setLabel((cur) => cur || f.name.replace(/\.[^.]+$/, ""));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || String(err);
      setPending((p) => (p ? { ...p, error: msg } : { file: f, progress: 0, error: msg }));
      toast.error(msg, { duration: 10000 });
    }
  };

  const saveSlot = async () => {
    if (!pending?.mediaId) return toast.error("Sube un archivo primero");
    if (!days.length) return toast.error("Elige al menos un día");
    if (sameTime) return toast.error("La hora de inicio y fin no pueden ser iguales");
    setSaving(true);
    try {
      await apiAdmin.post(`/tvs/${tv.id}/quick-content`, {
        media_id: pending.mediaId,
        days_of_week: days,
        start_minute: startMinutes,
        end_minute: endMinutes,
        label: label.trim(),
        item_duration: itemDuration,
      });
      toast.success("Contenido programado");
      setPending(null);
      setLabel("");
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (sid) => {
    if (!window.confirm("¿Eliminar este horario y su contenido?")) return;
    try {
      await apiAdmin.delete(`/tvs/${tv.id}/quick-content/${sid}`);
      setItems((prev) => prev.filter((x) => x.id !== sid));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Per-item edit modal state (open one row at a time)
  const [editingId, setEditingId] = useState(null);
  const editingItem = items.find((x) => x.id === editingId) || null;

  const saveEditedItem = async (patch) => {
    if (!editingId) return;
    try {
      const { data: updated } = await apiAdmin.patch(`/tvs/${tv.id}/quick-content/${editingId}`, patch);
      setItems((prev) => prev.map((x) => (x.id === editingId ? { ...x, ...updated } : x)));
      setEditingId(null);
      toast.success("Horario actualizado");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ── 24h mode helpers ──────────────────────────────────────────────────────
  const upload24Files = async (files) => {
    if (!files?.length) return;
    // Initialize batch UI
    const batch = Array.from(files).map((f) => ({ file: f, progress: 0, mediaId: null, error: null }));
    setPendingBatch(batch);

    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const { data: init } = await apiAdmin.post("/media/upload-url", {
          business_id: businessId,
          filename: f.name,
          content_type: f.type || "application/octet-stream",
        });
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", init.signed_url, true);
          xhr.setRequestHeader("Content-Type", f.type || "application/octet-stream");
          xhr.setRequestHeader("x-upsert", "true");
          xhr.upload.onprogress = (ev) => {
            if (!ev.lengthComputable) return;
            setPendingBatch((b) =>
              b.map((it, idx) => (idx === i ? { ...it, progress: Math.round((ev.loaded / ev.total) * 100) } : it))
            );
          };
          xhr.onload = () =>
            xhr.status >= 200 && xhr.status < 300
              ? resolve(null)
              : reject(new Error(`Upload failed (${xhr.status})`));
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.send(f);
        });
        const { data: media } = await apiAdmin.post("/media/upload-finalize", {
          media_id: init.media_id,
          object_path: init.object_path,
          business_id: businessId,
          filename: f.name,
          content_type: f.type || "application/octet-stream",
          size: f.size,
        });
        uploaded.push(media.id);
        setPendingBatch((b) =>
          b.map((it, idx) => (idx === i ? { ...it, progress: 100, mediaId: media.id } : it))
        );
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || String(err);
        setPendingBatch((b) => b.map((it, idx) => (idx === i ? { ...it, error: msg } : it)));
      }
    }

    if (uploaded.length === 0) {
      toast.error("Ningún archivo se subió correctamente");
      return;
    }

    // Attach them all to the 24h playlist
    try {
      await apiAdmin.post(`/tvs/${tv.id}/quick-content/24h`, {
        media_ids: uploaded,
        item_duration: itemDuration24,
      });
      toast.success(`${uploaded.length} ${uploaded.length === 1 ? "archivo agregado" : "archivos agregados"} al modo 24 horas`);
      setPendingBatch([]);
      await reload24();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handle24Files = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    upload24Files(files);
  };

  const onPick24Files = () => fileInputRef24.current?.click();

  const remove24Item = async (mediaId) => {
    if (!window.confirm("¿Quitar este archivo del bucle 24 horas?")) return;
    try {
      await apiAdmin.delete(`/tvs/${tv.id}/quick-content/24h/${mediaId}`);
      setAlwaysOnMedia((prev) => prev.filter((m) => m.id !== mediaId));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const update24Duration = async (seconds) => {
    setItemDuration24(seconds);
    if (alwaysOnMedia.length === 0) return; // No playlist yet
    // Reuse POST with empty media_ids? No — there's no PATCH endpoint. We send a single re-add
    // by POSTing the first media ID with the new duration (the backend updates item_duration).
    try {
      await apiAdmin.post(`/tvs/${tv.id}/quick-content/24h`, {
        media_ids: [alwaysOnMedia[0].id],
        item_duration: seconds,
      });
    } catch { /* silent */ }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Subir contenido para {tv?.name}</DialogTitle>
        <DialogDescription>
          Programa contenido por horario, o activa el modo 24 horas y sale todo el día.
        </DialogDescription>
      </DialogHeader>

      {/* Mode toggle */}
      <div className="mt-3 flex gap-2 p-1 bg-slate-100 rounded-full" data-testid="quick-content-mode-toggle">
        <button
          type="button"
          onClick={() => setMode24h(false)}
          className={`flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-full text-sm font-medium transition-all ${
            !mode24h ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="mode-schedule"
        >
          <ClockIcon className="h-3.5 w-3.5" /> Programar por horario
        </button>
        <button
          type="button"
          onClick={() => setMode24h(true)}
          className={`flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-full text-sm font-medium transition-all ${
            mode24h ? "bg-red-500 text-white shadow-sm shadow-red-500/30" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="mode-24h"
        >
          <Zap className="h-3.5 w-3.5" /> 24 horas
        </button>
      </div>

      {mode24h ? (
        <Mode24Section
          tv={tv}
          media={alwaysOnMedia}
          duration={itemDuration24}
          onChangeDuration={update24Duration}
          onAdd={onPick24Files}
          onRemove={remove24Item}
          pendingBatch={pendingBatch}
          fileInputRef={fileInputRef24}
          onFiles={handle24Files}
        />
      ) : (
        <ScheduleSection
          items={items}
          loading={loading}
          pending={pending}
          fileInputRef={fileInputRef}
          handleFile={handleFile}
          onPickFile={onPickFile}
          setPending={setPending}
          days={days}
          toggleDay={toggleDay}
          startH={startH} setStartH={setStartH}
          startM={startM} setStartM={setStartM}
          startP={startP} setStartP={setStartP}
          endH={endH} setEndH={setEndH}
          endM={endM} setEndM={setEndM}
          endP={endP} setEndP={setEndP}
          label={label} setLabel={setLabel}
          itemDuration={itemDuration} setItemDuration={setItemDuration}
          sameTime={sameTime}
          saving={saving}
          saveSlot={saveSlot}
          removeItem={removeItem}
          onClose={onClose}
          onEdit={setEditingId}
        />
      )}

      {/* Edit dialog for an existing scheduled item */}
      <EditQuickItemModal
        item={editingItem}
        onClose={() => setEditingId(null)}
        onSave={saveEditedItem}
      />
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Sub-sections
// ──────────────────────────────────────────────────────────────────────────────

function Mode24Section({ tv, media, duration, onChangeDuration, onAdd, onRemove, pendingBatch, fileInputRef, onFiles }) {
  return (
    <section className="mt-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={onFiles}
        data-testid="quick-content-24h-file-input"
      />

      <div className="rounded-xl bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 border border-red-100 px-4 py-3 text-sm text-red-900 flex items-start gap-3">
        <Zap className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">Modo 24 horas activo</div>
          <div className="text-xs mt-0.5 text-red-700/80">
            Las imágenes/videos que subas aquí salen las 24 horas todos los días. Puedes subir múltiples archivos a la vez — se reproducen en loop.
          </div>
        </div>
      </div>

      {/* Drop zone */}
      <button
        type="button"
        onClick={onAdd}
        className="mt-4 w-full border-2 border-dashed border-slate-300 hover:border-red-400 hover:bg-red-50/50 rounded-xl py-6 px-4 flex flex-col items-center gap-1.5 text-slate-600 hover:text-red-700 transition-colors"
        data-testid="quick-content-24h-upload-button"
      >
        <Upload className="h-5 w-5" />
        <span className="text-sm font-medium">Subir foto(s) o video(s)</span>
        <span className="text-xs text-slate-500">Selecciona uno o varios archivos</span>
      </button>

      {/* Upload progress for current batch */}
      {pendingBatch.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="quick-content-24h-batch">
          {pendingBatch.map((it, idx) => (
            <li key={idx} className="rounded-lg border border-slate-200 bg-white p-2.5 flex items-center gap-3">
              <div className="h-8 w-8 rounded-md bg-slate-100 grid place-items-center text-slate-600 shrink-0">
                {(it.file?.type || "").startsWith("video/") ? <Film className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-slate-900 truncate">{it.file?.name}</div>
                {it.error ? (
                  <div className="text-[11px] text-rose-600 truncate">{it.error}</div>
                ) : it.mediaId ? (
                  <div className="text-[11px] text-emerald-600 flex items-center gap-1"><Check className="h-3 w-3" /> Listo</div>
                ) : (
                  <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500 transition-all" style={{ width: `${it.progress}%` }} />
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Existing 24h playlist items */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
            En el bucle 24 horas · {media.length} {media.length === 1 ? "item" : "items"}
          </div>
          <div className="flex items-center gap-1.5">
            <Label htmlFor="dur24" className="text-[11px] text-slate-500">Seg/foto</Label>
            <Input
              id="dur24"
              type="number"
              min={2}
              max={300}
              value={duration}
              onChange={(e) => onChangeDuration(Math.max(2, parseInt(e.target.value || "8", 10)))}
              className="h-7 w-16 text-xs"
              data-testid="quick-content-24h-duration"
            />
          </div>
        </div>
        {media.length === 0 ? (
          <div className="text-xs text-slate-500 italic py-3">
            Aún no hay contenido en el bucle 24h. Sube archivos arriba.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="quick-content-24h-list">
            {media.map((m) => {
              const isVideo = (m.content_type || "").startsWith("video/");
              return (
                <li
                  key={m.id}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5"
                  data-testid={`quick-content-24h-item-${m.id}`}
                >
                  <div className="h-10 w-10 rounded-md bg-slate-100 grid place-items-center shrink-0 text-slate-600">
                    {isVideo ? <Film className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{m.filename}</div>
                    <div className="text-[11px] text-slate-500">{isVideo ? "Video" : "Foto"}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(m.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`quick-content-24h-item-${m.id}-remove`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function ScheduleSection({
  items, loading, pending, fileInputRef, handleFile, onPickFile, setPending,
  days, toggleDay, startH, setStartH, startM, setStartM, startP, setStartP,
  endH, setEndH, endM, setEndM, endP, setEndP, label, setLabel,
  itemDuration, setItemDuration, sameTime, saving, saveSlot, removeItem, onClose, onEdit,
}) {
  return (
    <>
      <section className="mt-2">
        <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
          Programado en esta TV
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          ) : items.length === 0 ? (
            <div className="text-xs text-slate-500 italic py-3">
              Aún no hay contenido programado. Súbelo abajo.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="quick-content-list">
              {items.map((it) => {
                const m = it.media;
                const isVideo = (m?.content_type || "").startsWith("video/");
                return (
                  <li
                    key={it.id}
                    className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5"
                    data-testid={`quick-content-item-${it.id}`}
                  >
                    <div className="h-10 w-10 rounded-md bg-slate-200 grid place-items-center shrink-0 text-slate-600">
                      {isVideo ? <Film className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {it.label || m?.filename || "Item"}
                      </div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-x-2 gap-y-0.5">
                        <span className="font-mono">
                          {minutesToHHMM(it.start_minute)}–{minutesToHHMM(it.end_minute)}
                        </span>
                        <span>·</span>
                        <span>{it.days_of_week.map((d) => DAY_LABELS[d]).join(" · ")}</span>
                        {m?.filename && (
                          <>
                            <span>·</span>
                            <span className="truncate">{m.filename}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(it.id)}
                      className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                      data-testid={`quick-content-item-${it.id}-edit`}
                      title="Editar día y hora"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(it.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`quick-content-item-${it.id}-remove`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Add new */}
        <section className="mt-6 pt-5 border-t border-slate-100">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-3">
            Agregar nuevo
          </div>

          {/* Upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={handleFile}
            data-testid="quick-content-file-input"
          />
          {!pending ? (
            <button
              type="button"
              onClick={onPickFile}
              className="w-full border-2 border-dashed border-slate-300 hover:border-slate-900 hover:bg-slate-50 rounded-xl py-6 px-4 flex flex-col items-center gap-1.5 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="quick-content-upload-button"
            >
              <Upload className="h-5 w-5" />
              <span className="text-sm font-medium">Subir foto o video</span>
              <span className="text-xs text-slate-500">Click o arrastra un archivo</span>
            </button>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-slate-100 grid place-items-center text-slate-600 shrink-0">
                {(pending.file?.type || "").startsWith("video/") ? <Film className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{pending.file?.name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <span>{fmtSize(pending.file?.size || 0)}</span>
                  {pending.error ? (
                    <span className="text-rose-600 truncate">· {pending.error}</span>
                  ) : pending.mediaId ? (
                    <span className="text-emerald-600 inline-flex items-center gap-1">
                      <Check className="h-3 w-3" /> Listo
                    </span>
                  ) : (
                    <span>· Subiendo {pending.progress}%</span>
                  )}
                </div>
                {!pending.mediaId && !pending.error && (
                  <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-slate-900 transition-all"
                      style={{ width: `${pending.progress}%` }}
                    />
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPending(null)}
                className="shrink-0 text-slate-400 hover:text-slate-700"
                data-testid="quick-content-clear-upload"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Days */}
          <div className="mt-5">
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              Días de la semana
            </Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {DAY_LABELS.map((lbl, idx) => {
                const on = days.includes(idx);
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => toggleDay(idx)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      on
                        ? "bg-red-500 border-red-500 text-white"
                        : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                    }`}
                    data-testid={`quick-content-day-${idx}`}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time range */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TimePicker
              label="Inicio"
              hour={startH} setHour={setStartH}
              minute={startM} setMinute={setStartM}
              period={startP} setPeriod={setStartP}
              testidPrefix="quick-start"
            />
            <TimePicker
              label="Fin"
              hour={endH} setHour={setEndH}
              minute={endM} setMinute={setEndM}
              period={endP} setPeriod={setEndP}
              testidPrefix="quick-end"
            />
          </div>

          {/* Extra options */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="quick-label" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Etiqueta (opcional)
              </Label>
              <Input
                id="quick-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Ej: Lunch promo"
                className="mt-1.5 h-9"
                data-testid="quick-content-label"
              />
            </div>
            <div>
              <Label htmlFor="quick-duration" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Duración por foto (segundos)
              </Label>
              <Input
                id="quick-duration"
                type="number"
                min={2}
                max={300}
                value={itemDuration}
                onChange={(e) => setItemDuration(Math.max(2, parseInt(e.target.value || "8", 10)))}
                className="mt-1.5 h-9"
                data-testid="quick-content-duration"
              />
              <div className="text-[11px] text-slate-400 mt-1">Solo afecta a fotos. Los videos usan su duración real.</div>
            </div>
          </div>

          {sameTime && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              La hora de inicio y fin son iguales — ajusta una de las dos.
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-2">
            <Button variant="outline" onClick={onClose} data-testid="quick-content-cancel">
              Cerrar
            </Button>
            <Button
              onClick={saveSlot}
              disabled={!pending?.mediaId || saving || sameTime || !days.length}
              className="bg-red-500 hover:bg-red-600"
              data-testid="quick-content-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Programar contenido
            </Button>
          </div>
        </section>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Edit modal — change day/hour of an already-scheduled item.
// ──────────────────────────────────────────────────────────────────────────────

function EditQuickItemModal({ item, onClose, onSave }) {
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="quick-content-edit-dialog">
        {item && <EditQuickItemBody key={item.id} item={item} onClose={onClose} onSave={onSave} />}
      </DialogContent>
    </Dialog>
  );
}

function EditQuickItemBody({ item, onClose, onSave }) {
  const initial = item;
  const sm0 = initial.start_minute || 0;
  const em0 = initial.end_minute || 0;
  const to12 = (mins) => {
    const h24 = Math.floor(mins / 60);
    const min = mins % 60;
    const period = h24 < 12 ? "AM" : "PM";
    const h12 = ((h24 + 11) % 12) + 1;
    // snap minutes to nearest 5 so the Select shows a valid value
    const snapped = Math.round(min / 5) * 5;
    const final = snapped >= 60 ? 55 : snapped;
    return [h12, final, period];
  };
  const [sh0, smin0, sp0] = to12(sm0);
  const [eh0, emin0, ep0] = to12(em0);

  const [days, setDays] = useState(initial.days_of_week || []);
  const [startH, setStartH] = useState(sh0);
  const [startM, setStartM] = useState(smin0);
  const [startP, setStartP] = useState(sp0);
  const [endH, setEndH] = useState(eh0);
  const [endM, setEndM] = useState(emin0);
  const [endP, setEndP] = useState(ep0);
  const [label, setLabel] = useState(initial.label || "");
  const [duration, setDuration] = useState((initial.media?.media_type === "image" && initial.item_duration) || 8);
  const [saving, setSaving] = useState(false);

  const startMinutes = to24(startH, startP) * 60 + startM;
  const endMinutes = to24(endH, endP) * 60 + endM;
  const sameTime = startMinutes === endMinutes;

  const toggleDay = (d) =>
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()));

  const handleSave = async () => {
    if (!days.length) return toast.error("Elige al menos un día");
    if (sameTime) return toast.error("Inicio y fin no pueden ser iguales");
    setSaving(true);
    try {
      await onSave({
        days_of_week: days,
        start_minute: startMinutes,
        end_minute: endMinutes,
        label: label.trim(),
        item_duration: duration,
      });
    } finally {
      setSaving(false);
    }
  };

  const m = initial.media;
  const isVideo = (m?.content_type || "").startsWith("video/");

  return (
    <>
      <DialogHeader>
        <DialogTitle>Editar día y hora</DialogTitle>
        <DialogDescription>
          Cambia cuándo aparece este contenido en la TV. El archivo no cambia.
        </DialogDescription>
      </DialogHeader>

      {m && (
        <div className="mt-2 flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2.5">
          <div className="h-10 w-10 rounded-md bg-slate-200 grid place-items-center text-slate-600 shrink-0">
            {isVideo ? <Film className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">{m.filename}</div>
            <div className="text-[11px] text-slate-500">{isVideo ? "Video" : "Foto"}</div>
          </div>
        </div>
      )}

      {/* Days */}
      <div className="mt-4">
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
          Días de la semana
        </Label>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {DAY_LABELS.map((lbl, idx) => {
            const on = days.includes(idx);
            return (
              <button
                key={idx}
                type="button"
                onClick={() => toggleDay(idx)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  on
                    ? "bg-red-500 border-red-500 text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                }`}
                data-testid={`edit-day-${idx}`}
              >
                {lbl}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time range */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TimePicker
          label="Inicio"
          hour={startH} setHour={setStartH}
          minute={startM} setMinute={setStartM}
          period={startP} setPeriod={setStartP}
          testidPrefix="edit-start"
        />
        <TimePicker
          label="Fin"
          hour={endH} setHour={setEndH}
          minute={endM} setMinute={setEndM}
          period={endP} setPeriod={setEndP}
          testidPrefix="edit-end"
        />
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="edit-label" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Etiqueta
          </Label>
          <Input
            id="edit-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="mt-1.5 h-9"
            data-testid="edit-label"
          />
        </div>
        <div>
          <Label htmlFor="edit-duration" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Duración (s)
          </Label>
          <Input
            id="edit-duration"
            type="number"
            min={2}
            max={300}
            value={duration}
            onChange={(e) => setDuration(Math.max(2, parseInt(e.target.value || "8", 10)))}
            className="mt-1.5 h-9"
            data-testid="edit-duration"
          />
        </div>
      </div>

      {sameTime && (
        <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          La hora de inicio y fin son iguales.
        </div>
      )}

      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Cancelar</Button>
        <Button
          onClick={handleSave}
          disabled={saving || sameTime || !days.length}
          className="bg-red-500 hover:bg-red-600"
          data-testid="edit-save"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
          Guardar cambios
        </Button>
      </div>
    </>
  );
}

function TimePicker({ label, hour, setHour, minute, setMinute, period, setPeriod, testidPrefix }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1.5 grid grid-cols-3 gap-1.5">
        <Select value={String(hour)} onValueChange={(v) => setHour(parseInt(v, 10))}>
          <SelectTrigger className="h-9 text-sm" data-testid={`${testidPrefix}-hour`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HOURS_12.map((h) => (
              <SelectItem key={h} value={String(h)}>{h}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(minute)} onValueChange={(v) => setMinute(parseInt(v, 10))}>
          <SelectTrigger className="h-9 text-sm" data-testid={`${testidPrefix}-minute`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MINUTES_5.map((m) => (
              <SelectItem key={m} value={String(m)}>{String(m).padStart(2, "0")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="h-9 text-sm" data-testid={`${testidPrefix}-period`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
